import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, useTheme, API } from "../App";
import axios from "axios";
import { Button } from "./ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { 
  LayoutDashboard, 
  ArrowLeftRight, 
  CreditCard, 
  Target,
  BarChart3,
  User,
  LogOut,
  Menu,
  ShieldCheck,
  Users,
  Eye,
  ArrowLeft,
  Flame,
  Building2,
  Landmark
} from "lucide-react";
import AnimatedLogo from "./AnimatedLogo";
import FloatingTransaction from "./FloatingTransaction";
import ServiceAds from "./ServiceAds";
import DashboardTutorial from "./DashboardTutorial";

const Layout = ({ children }) => {
  const { user, logout, isImpersonating, impersonatedUser, stopImpersonation } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [streak, setStreak] = useState(0);

  const isFreeUser = user?.user_tier === 'sin_asesoria';

  useEffect(() => {
    if (user) {
      axios.get(`${API}/streak`, { withCredentials: true })
        .then(r => setStreak(r.data.streak || 0))
        .catch(() => {});
    }
    const handler = () => {
      axios.get(`${API}/streak`, { withCredentials: true })
        .then(r => setStreak(r.data.streak || 0))
        .catch(() => {});
    };
    window.addEventListener('transaction-created', handler);
    return () => window.removeEventListener('transaction-created', handler);
  }, [user]);

  const navigation = [
    { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
    { name: "Transacciones", href: "/transactions", icon: ArrowLeftRight },
    { name: "Deudas", href: "/debts", icon: CreditCard },
    { name: "Ahorros", href: "/savings", icon: Target },
    { name: "Reportes", href: "/reports", icon: BarChart3 },
    { name: "Comunidad", href: "/community", icon: Users },
  ];

  // Add admin link if user is admin
  if (user?.is_admin) {
    navigation.push({ name: "Admin", href: "/admin", icon: ShieldCheck });
  }

  const isActive = (href) => location.pathname === href;

  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = "/";
  };

  const handleStopImpersonation = async () => {
    await stopImpersonation();
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-[#141b2d]">
      {/* Impersonation Banner */}
      {isImpersonating && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-amber-500 text-black py-2 px-4" data-testid="impersonation-banner">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4" />
              <span className="text-sm font-semibold">
                Modo Asesor: Viendo como {impersonatedUser?.name || user?.name}
              </span>
              <span className="text-xs opacity-75">({impersonatedUser?.email || user?.email})</span>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="bg-black/10 border-black/30 text-black hover:bg-black/20 h-7 text-xs"
              onClick={handleStopImpersonation}
              data-testid="stop-impersonation-btn"
            >
              <ArrowLeft className="w-3 h-3 mr-1" />
              Volver al Panel Admin
            </Button>
          </div>
        </div>
      )}
      {/* Navigation Header - FIXED position for all browsers */}
      <header className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b ${
        isImpersonating ? 'mt-[40px]' : ''
      } ${
        theme === 'light' ? 'bg-white/95 border-gray-200' : 'bg-[#141b2d]/95 border-[#2a3444]'
      }`} data-testid="app-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-4">
              <Link to={location.pathname.startsWith('/prestamos') ? '/prestamos' : '/dashboard'} className="flex items-center gap-3">
                {location.pathname.startsWith('/prestamos') ? (
                  <div className="flex items-center gap-2">
                    <span className="text-[#D4AF37] font-bold text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>LD</span>
                    <span className="text-white font-medium text-sm" style={{ fontFamily: 'Playfair Display, serif' }}>Holdings</span>
                  </div>
                ) : (
                  <AnimatedLogo size="small" />
                )}
              </Link>
              <div className="hidden md:flex items-center gap-1.5 ml-2 border-l border-[#2a3444] pl-3">
                <Link 
                  to="/"
                  className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md transition-colors ${
                    theme === 'light' 
                      ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100' 
                      : 'text-gray-500 hover:text-gray-300 hover:bg-[#1a2332]'
                  }`}
                  data-testid="nav-to-holdings"
                >
                  <Building2 size={14} />
                  Holdings
                </Link>
                {(user?.is_admin_holdings || user?.is_admin) && (
                  <Link 
                    to="/prestamos"
                    className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md transition-colors ${
                      location.pathname.startsWith('/prestamos')
                        ? 'bg-[#D4AF37]/10 text-[#D4AF37]'
                        : theme === 'light' 
                          ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100' 
                          : 'text-gray-500 hover:text-gray-300 hover:bg-[#1a2332]'
                    }`}
                    data-testid="nav-to-admin-module"
                  >
                    <Landmark size={14} />
                    Holdings
                  </Link>
                )}
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navigation.map((item) => (
                <Link key={item.name} to={item.href}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`rounded-md transition-all ${
                      isActive(item.href) 
                        ? "bg-[#D4AF37]/10 text-[#D4AF37]" 
                        : theme === 'light'
                          ? "text-gray-600 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10"
                          : "text-gray-400 hover:text-white hover:bg-[#1a2332]"
                    }`}
                  >
                    <item.icon className="w-4 h-4 mr-2" />
                    {item.name}
                  </Button>
                </Link>
              ))}
            </nav>

            {/* User Menu & Mobile Toggle */}
            <div className="flex items-center gap-2">
              {/* Streak */}
              {streak > 0 && (
                <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-orange-500/10 border border-orange-500/20" data-testid="streak-badge">
                  <Flame className="w-4 h-4 text-orange-500" />
                  <span className="text-sm font-bold text-orange-500 font-mono">{streak}</span>
                </div>
              )}
              {/* User Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className={`relative h-9 w-9 rounded-full border ${theme === 'light' ? 'border-gray-200' : 'border-[#2a3444]'}`}>
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback className="bg-[#D4AF37] text-[#141b2d] text-xs font-semibold">
                        {getInitials(user?.name)}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className={`w-56 ${theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#1a2332] border-[#2a3444]'}`} align="end">
                  <div className="flex items-center gap-2 p-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user?.picture} alt={user?.name} />
                      <AvatarFallback className="bg-[#D4AF37] text-[#141b2d] text-xs font-semibold">
                        {getInitials(user?.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col">
                      <p className={`text-sm font-medium ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}>{user?.name}</p>
                      <p className="text-xs text-gray-400 truncate">{user?.email}</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator className={theme === 'light' ? 'bg-gray-200' : 'bg-[#2a3444]'} />
                  <DropdownMenuItem asChild className={theme === 'light' ? 'text-gray-700 focus:text-[#D4AF37] focus:bg-[#D4AF37]/10' : 'text-gray-300 focus:text-white focus:bg-[#2a3444]'}>
                    <Link to="/profile" className="cursor-pointer">
                      <User className="w-4 h-4 mr-2" />
                      Mi Perfil
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className={theme === 'light' ? 'bg-gray-200' : 'bg-[#2a3444]'} />
                  <DropdownMenuItem 
                    className={theme === 'light' ? 'text-red-500 focus:text-red-600 focus:bg-red-50 cursor-pointer' : 'text-red-400 focus:text-red-300 focus:bg-[#2a3444] cursor-pointer'}
                    onClick={handleLogout}
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Cerrar sesion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile Menu Toggle */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild className="md:hidden">
                  <Button variant="ghost" size="icon" className={theme === 'light' ? 'text-gray-600 hover:text-[#D4AF37]' : 'text-gray-400 hover:text-white'}>
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className={`w-72 ${theme === 'light' ? 'bg-white border-gray-200' : 'bg-[#1a2332] border-[#2a3444]'}`}>
                  <div className="flex flex-col gap-2 mt-8">
                    {navigation.map((item) => (
                      <Link 
                        key={item.name} 
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <Button
                          variant="ghost"
                          className={`w-full justify-start rounded-md ${
                            isActive(item.href) 
                              ? "bg-[#D4AF37]/10 text-[#D4AF37]" 
                              : theme === 'light'
                                ? "text-gray-600 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10"
                                : "text-gray-400 hover:text-white hover:bg-[#2a3444]"
                          }`}
                        >
                          <item.icon className="w-5 h-5 mr-3" />
                          {item.name}
                        </Button>
                      </Link>
                    ))}
                    <div className={`border-t my-4 ${theme === 'light' ? 'border-gray-200' : 'border-[#2a3444]'}`} />
                    <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className={`w-full justify-start rounded-md ${
                        theme === 'light' ? 'text-gray-600 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10' : 'text-gray-400 hover:text-white hover:bg-[#2a3444]'
                      }`}>
                        <Building2 className="w-5 h-5 mr-3" />
                        LD Holdings
                      </Button>
                    </Link>
                    {(user?.is_admin_holdings || user?.is_admin) && (
                      <Link to="/prestamos" onClick={() => setMobileMenuOpen(false)}>
                        <Button variant="ghost" className={`w-full justify-start rounded-md ${
                          theme === 'light' ? 'text-gray-600 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10' : 'text-gray-400 hover:text-white hover:bg-[#2a3444]'
                        }`}>
                          <Landmark className="w-5 h-5 mr-3" />
                          LD Holdings Admin
                        </Button>
                      </Link>
                    )}
                    <Link to="/profile" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className={`w-full justify-start rounded-md ${
                        theme === 'light' ? 'text-gray-600 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10' : 'text-gray-400 hover:text-white hover:bg-[#2a3444]'
                      }`}>
                        <User className="w-5 h-5 mr-3" />
                        Mi Perfil
                      </Button>
                    </Link>
                    <Button 
                      variant="ghost" 
                      className={`w-full justify-start rounded-md ${
                        theme === 'light' ? 'text-red-500 hover:text-red-600 hover:bg-red-50' : 'text-red-400 hover:text-red-300 hover:bg-[#2a3444]'
                      }`}
                      onClick={handleLogout}
                    >
                      <LogOut className="w-5 h-5 mr-3" />
                      Cerrar sesión
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content - offset for fixed header */}
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 ${isImpersonating ? 'pt-[calc(2rem+104px)]' : 'pt-[calc(2rem+64px)]'}`}>
        {/* Mobile ad banner for free users */}
        {isFreeUser && (
          <div className="lg:hidden">
            <ServiceAds variant="banner" count={1} />
          </div>
        )}
        
        {isFreeUser ? (
          <div className="flex gap-6">
            <main className="flex-1 min-w-0">
              {children}
            </main>
            {/* Desktop sidebar ads */}
            <aside className="hidden lg:block w-[220px] shrink-0">
              <div className="sticky top-24">
                <ServiceAds variant="sidebar" count={3} />
              </div>
            </aside>
          </div>
        ) : (
          <main>{children}</main>
        )}
      </div>

      {/* Dashboard Tutorial - shows once after survey */}
      <DashboardTutorial />

      {/* Footer */}
      <footer className={`border-t mt-12 ${theme === 'light' ? 'border-gray-200' : 'border-[#2a3444]'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <AnimatedLogo size="small" />
            <p className="text-sm text-gray-400 text-center sm:text-right">
              Decisiones financieras inteligentes, resultados reales
            </p>
          </div>
        </div>
      </footer>

      {/* Floating Transaction Button - always visible */}
      <FloatingTransaction />
    </div>
  );
};

export default Layout;
